import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import {StaticFormService} from './static-form.service'
import {concatMap, Observable, Subject} from 'rxjs'
import {map, startWith, takeUntil} from 'rxjs/operators'
import {Offering, StaticComponentInstance, StaticFormGroup, StaticFormLayoutConfig} from './static-form.types'
import {JobRequestDetail_CalculatePriceRangeGql, PriceRange} from '../../../../../../__generated__/types'

@Component({
  selector: 'bgo-static-form',
  templateUrl: './static-form.component.html',
  styleUrls: ['./static-form.component.css'],
})
export class StaticFormComponent implements AfterViewInit, OnDestroy {
  @Input({required: true}) offering!: Offering
  @Input({required: true}) formGroup!: StaticFormGroup
  @Output() activated = new EventEmitter<boolean>()
  @Output() instantiated = new EventEmitter<StaticComponentInstance>()

  @ViewChild('staticFormContainer', {read: ViewContainerRef}) private staticFormContainer!: ViewContainerRef

  protected layout$?: Observable<StaticFormLayoutConfig>
  protected priceRange$?: Observable<PriceRange>

  private readonly unsubscribe$ = new Subject<void>()

  constructor(
    private readonly staticFormService: StaticFormService,
    private readonly calculatePriceRangeGql: JobRequestDetail_CalculatePriceRangeGql,
  ) {}

  ngAfterViewInit(): void {
    this.renderStaticForm()
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
    this.staticFormContainer.clear()
  }

  private renderStaticForm(): void {
    const ref = this.staticFormService.renderStaticFormComponent(this.staticFormContainer, this.offering)
    if (ref) {
      // setup the form group
      const form = this.formGroup
      ref.instance.form = form

      // setup price range calculations
      this.priceRange$ = this.setupPriceRangeCalculator(form, ref.instance)
      if (ref.instance.setPriceRange) {
        ref.instance.setPriceRange(this.priceRange$)
      }

      // listen to layout$ changes from the instance, if it's implemented
      if (ref.instance.layout$) {
        this.layout$ = ref.instance.layout$()
      }

      // emit activated event when form is valid
      form.statusChanges.pipe(startWith(form.status), takeUntil(this.unsubscribe$)).subscribe(status => {
        this.activated.emit(status === 'VALID')
      })

      // component is ready
      this.instantiated.emit(ref.instance)
    }
  }

  private setupPriceRangeCalculator(form: StaticFormGroup, instance: StaticComponentInstance) {
    return form.valueChanges.pipe(
      startWith(form.value),
      map(() => instance.questionsInput()),
      // Apollo by default doesn't send the same requests again while it's still in flight, which is a problem if the
      // client is clicking through the options quickly on a slow connection. i.e. same requests will be lost/dropped.
      // Using concatMap we're effectively queueing all the clicks/requests, and processing them in order as soon as
      // the previous request completes.
      // TODO: should use switchMap to cancel previous unnecessary requests in this case ?
      // TODO Also, should we debounce to avoid spam when there's an input field for instance ?
      concatMap(questions => this.calculatePriceRangeGql.fetch({jobQuestions: questions})),
      map(value => value.data.calculatePriceRange),
    )
  }
}
